import React from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, FileSignature, Target, Rocket, Trophy, ArrowRight, RotateCcw } from 'lucide-react';
import { Reveal, SectionHeading } from './Reveal';
import { BOOKING_URL, CTA_LABEL } from '../data/constants';

const steps = [
  { icon: MessageCircle, title: 'Erstgespräch', duration: '30 Min', text: 'Kostenlos & unverbindlich. Wir verstehen deine Situation und zeigen dir, wie HIREKA arbeitet.' },
  { icon: FileSignature, title: 'Vermittlungsvertrag', duration: '15 Min', text: 'Klare Konditionen, Festpreis, keine versteckten Kosten. Ein Vertrag, der beide Seiten schützt.' },
  { icon: Target, title: 'Positionsbriefing', duration: '~60 Min', text: 'Fachlich und menschlich: Wer passt ins Team? Das wichtigste Gespräch im gesamten Prozess.', highlight: true },
  { icon: Rocket, title: 'Kandidat in 24h', duration: '24 h', text: 'Du erhältst das erste interviewte, gebriefte Kandidatenprofil – garantiert innerhalb von 24 Stunden.' },
  { icon: Trophy, title: 'Probezeit bestanden', duration: '6 Monate', text: 'Perfekt – dein Credit ist eingelöst. Falls nicht: Dein Credit bleibt bestehen und wir besetzen kostenlos nach.', loop: true },
];

const ProcessTimeline = () => (
  <section id="prozess" className="relative bg-cream py-24 lg:py-32 overflow-hidden" data-testid="process-section">
    <div className="absolute -left-40 top-1/2 w-[500px] h-[500px] rounded-full bg-violet-200/40 blur-[120px] pointer-events-none" />
    <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
      <SectionHeading
        eyebrow="So funktioniert's"
        title={<>Vom ersten Gespräch zum <span className="text-violet-gradient">besetzten Platz</span> – in 5 Schritten.</>}
        subtitle="Kein Hin und Her, keine endlosen Abstimmungsschleifen. Ein klarer Prozess mit einem Ziel: die richtige Person in deinem Team."
      />

      <div className="relative mt-20">
        <div className="hidden lg:block absolute top-8 left-[10%] right-[10%] h-px bg-stone-300">
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 1.6, ease: [0.2, 0.8, 0.2, 1] }}
            className="h-full origin-left bg-gradient-to-r from-violet-600 via-violet-500 to-amber-400"
          />
        </div>

        <div className="grid lg:grid-cols-5 gap-10 lg:gap-6">
          {steps.map((s, i) => (
            <Reveal key={i} delay={i * 0.12} className="relative flex lg:flex-col gap-5 lg:gap-0" data-testid={`process-step-${i}`}>
              <div className="relative shrink-0 lg:mx-auto">
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center shadow-xl relative z-10 ${s.highlight ? 'bg-gradient-to-br from-amber-400 to-amber-500 shadow-amber-500/30' : 'bg-obsidian shadow-violet-900/30'}`}>
                  <s.icon className={`w-7 h-7 ${s.highlight ? 'text-obsidian' : 'text-amber-400'}`} />
                </div>
                <span className="absolute -top-3 -right-3 z-20 w-7 h-7 rounded-full bg-white border border-stone-200 text-[11px] font-bold text-violet-700 flex items-center justify-center shadow">
                  {i + 1}
                </span>
                {i < steps.length - 1 && <div className="lg:hidden absolute left-1/2 top-16 bottom-[-40px] w-px bg-stone-300 -translate-x-1/2" />}
              </div>

              <div className="lg:mt-8 lg:text-center">
                <div className="flex lg:justify-center items-center gap-2 mb-2">
                  <h3 className="text-lg md:text-xl font-bold text-slate-900">{s.title}</h3>
                </div>
                <span className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-full mb-3 ${s.highlight ? 'bg-amber-100 text-amber-800' : 'bg-violet-100 text-violet-700'}`}>{s.duration}</span>
                <p className="text-slate-600 text-sm leading-relaxed">{s.text}</p>
                {s.loop && (
                  <p className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-rose-600">
                    <RotateCcw className="w-3.5 h-3.5" /> Kostenlos zurück zu Schritt 4
                  </p>
                )}
                {s.highlight && <p className="mt-3 text-xs font-bold uppercase tracking-[0.18em] text-amber-600">Wichtigster Schritt</p>}
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      <Reveal delay={0.3} className="mt-20 text-center">
        <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="btn-lux px-8 py-4 text-base" data-testid="process-cta">
          {CTA_LABEL} <ArrowRight className="w-5 h-5" />
        </a>
        <p className="mt-4 text-sm text-slate-500">Schritt 1 dauert 30 Minuten. Kostenlos, unverbindlich.</p>
      </Reveal>
    </div>
  </section>
);

export default ProcessTimeline;
